import { useState, useEffect, useCallback } from "react";
import { getSupabaseClient } from "@/components/atomic-crm/providers/supabase/supabase";

const VAPID_PUBLIC_KEY = import.meta.env.VITE_VAPID_PUBLIC_KEY as string;

type PermissionState = "unsupported" | "denied" | "default" | "granted";

export interface PushNotificationState {
  permissionState: PermissionState;
  isSubscribed: boolean;
  isLoading: boolean;
  error: string | null;
  subscribe: () => Promise<void>;
  unsubscribe: () => Promise<void>;
}

/**
 * Manages Web Push subscription for the current salesperson.
 *
 * subscribe() → requests permission → subscribes browser → saves to push_subscriptions
 * unsubscribe() → removes from push_subscriptions → unsubscribes browser
 */
export function usePushNotifications(
  salesId: number | null,
): PushNotificationState {
  const [permissionState, setPermissionState] =
    useState<PermissionState>("default");
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Resolve initial state on mount
  useEffect(() => {
    if (!salesId) {
      setIsLoading(false);
      return;
    }

    if (!("PushManager" in window) || !("serviceWorker" in navigator)) {
      setPermissionState("unsupported");
      setIsLoading(false);
      return;
    }

    const permission = Notification.permission as PermissionState;
    setPermissionState(permission);

    navigator.serviceWorker.ready
      .then(async (reg) => {
        const existing = await reg.pushManager.getSubscription();
        setIsSubscribed(!!existing);
      })
      .catch(() => {
        // SW not ready yet — treat as not subscribed
      })
      .finally(() => setIsLoading(false));
  }, [salesId]);

  const subscribe = useCallback(async () => {
    if (!salesId) return;
    if (!VAPID_PUBLIC_KEY) {
      setError(
        "Push notifications are not configured (missing VAPID key). Contact your administrator.",
      );
      return;
    }
    setIsLoading(true);
    setError(null);

    try {
      const reg = await navigator.serviceWorker.ready;

      const permission = await Notification.requestPermission();
      setPermissionState(permission as PermissionState);
      if (permission !== "granted") {
        setIsLoading(false);
        return;
      }

      const subscription = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY),
      });

      const json = subscription.toJSON();
      const { error: dbError } = await getSupabaseClient()
        .from("push_subscriptions")
        .upsert(
          {
            sales_id: salesId,
            endpoint: subscription.endpoint,
            p256dh: json.keys?.p256dh ?? "",
            auth: json.keys?.auth ?? "",
            user_agent: navigator.userAgent.slice(0, 200),
            last_used_at: new Date().toISOString(),
          },
          { onConflict: "sales_id,endpoint" },
        );

      if (dbError) throw new Error(dbError.message);

      setIsSubscribed(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Subscription failed");
    } finally {
      setIsLoading(false);
    }
  }, [salesId]);

  const unsubscribe = useCallback(async () => {
    if (!salesId) return;
    setIsLoading(true);
    setError(null);

    try {
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.getSubscription();

      if (sub) {
        await getSupabaseClient()
          .from("push_subscriptions")
          .delete()
          .eq("sales_id", salesId)
          .eq("endpoint", sub.endpoint);

        await sub.unsubscribe();
      }

      setIsSubscribed(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Unsubscribe failed");
    } finally {
      setIsLoading(false);
    }
  }, [salesId]);

  return {
    permissionState,
    isSubscribed,
    isLoading,
    error,
    subscribe,
    unsubscribe,
  };
}

// Converts a URL-safe base64 VAPID public key to the Uint8Array the browser API expects
function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const rawData = atob(base64);
  return Uint8Array.from(rawData, (c) => c.charCodeAt(0));
}
